import React, {
  ReactNode, FC, useState, useCallback, useRef, useMemo, useEffect, cloneElement, ReactElement, HTMLAttributes,
} from 'react';
import { useDispatch } from 'react-redux';
import noop from 'lodash/noop';

import useScopedSelector from '../../../hooks/useScopedSelector';
import { StyledContentWrapper, StyledHeader } from './Collapsable.styles';
import findChildByType from '../../../utils/components/_findChildByType';

import { Target, Content } from './Collapsable.compounds';
import openCollapseById from '../../../actions/collapse-fn/openCollapseById';
import closeCollapseById from '../../../actions/collapse-fn/closeCollapseById';

export interface CollapsableProps extends HTMLAttributes<HTMLDivElement> {
  name?: string;
  /** The collapse content */
  children?: ReactNode;
  /** Controls if the Collapse is opened or closed. Marks this collapse as being "controlled". A controlled collapse won't integrate with Redux nor listen to actions. */
  isOpened?: boolean;
  /** Indicates that this collsable content should always be rendered. */
  forceRender?: boolean;
  /** Sets the initial value of `isOpened` */
  initialIsOpened?: boolean;
  /** Callback when the modal opens */
  onOpen?: () => void;
  /** Callback when the modal closes */
  onClose?: () => void;
  /** Type of element to use as the header. Default: div */
  headerAs?: string;
}

interface CollapsableInternalProps {
  /** @ignore */
  collapseId: number;
  /** @ignore */
  scope: [];
  /** @ignore */
  store: object;
}

export interface CollapsableComposedProps extends CollapsableProps, CollapsableInternalProps {}

export type CollapsableStatics = {
  Content: typeof Content;
  Target: typeof Target;
};

export interface CollapsableMock {}

const CollapsableDefaultProps = {
  forceRender: true,
};

/**
 * @mode developer
 */
const Collapsable: FC<CollapsableComposedProps> & CollapsableStatics = ({
  children, collapseId, name, initialIsOpened, headerAs, isOpened, scope, store, forceRender, onOpen, onClose, ...props
}) => {
  const dispatch = useDispatch();
  const targetFactory = findChildByType(children, Target)?.props.children;
  const content = findChildByType(children, Content) as ReactElement<{ children?: ReactElement | (() => ReactElement) }>;
  const timerRef = useRef<NodeJS.Timeout>();

  const isControlled = useMemo(() => typeof isOpened !== 'undefined', [isOpened]);
  const isOpenedFromState = useScopedSelector(`${name}.isOpened`);
  const isCollapsed = useMemo(() => isControlled ? !isOpened : !isOpenedFromState, [isControlled, isOpened, isOpenedFromState]);
  const [isCollapsing, setIsCollapsing] = useState(false);

  const [isInitialRender, setIsInitialRender] = useState(true);
  const initialContentMaxHeight = useMemo(() => isCollapsed ? '0' : 'none', [isCollapsed]);
  const initialContentDisplay = useMemo(() => isCollapsed ? 'none' : 'block', [isCollapsed]);
  const initialOverflow = useMemo(() => isCollapsed ? 'hidden' : 'visible', [isCollapsed]);
  const collapsableRef = useRef(null);

  const headerId = useMemo(() => `Collapse${collapseId}Header`, [collapseId]);
  const contentId = useMemo(() => `Collapse${collapseId}Content`, [collapseId]);

  const handleToggleCollapse = useCallback(() => {
    if (isCollapsed) {
      if (!isControlled) dispatch(openCollapseById(collapseId));
      if (onOpen) onOpen();
    } else {
      if (!isControlled) dispatch(closeCollapseById(collapseId));
      if (onClose) onClose();
    }
  }, [collapseId, dispatch, isCollapsed, isControlled, onClose, onOpen]);

  const target = useMemo(() => {
    if (!targetFactory) {
      return null;
    }

    const builtTarget = targetFactory({
      toggle: handleToggleCollapse,
      isCollapsed,
      collapseId,
      name,
    });

    return cloneElement(builtTarget, {
      'aria-controls': contentId,
      'aria-expanded': String(!isCollapsed),
      'data-testid': 'CollapsableToggleButton',
    });
  }, [collapseId, contentId, handleToggleCollapse, isCollapsed, name, targetFactory]);

  useEffect(() => {
    if (isInitialRender && collapsableRef?.current) {
      setIsInitialRender(false);
      const element = collapsableRef?.current;
      element.style.maxHeight = initialContentMaxHeight;
      element.style.display = initialContentDisplay;
      element.style.overflow = initialOverflow;
    } else if (!isInitialRender) {
      const element = collapsableRef?.current;
      element.style.display = 'block';
      const currentHeight = element.clientHeight;
      const targetHeight = isCollapsed ? 0 : element.scrollHeight;

      if (targetHeight === 0 && currentHeight === 0) {
        element.style.display = 'none';
      }

      const duration = Math.round(Math.sqrt(Math.abs(currentHeight - targetHeight)) / 0.65) / 100;
      // We use straight DOM manipulation as passing through React's render cycle is too slow.
      // First we want to set a height in pixel, without transition. Transition from an auto height is much slower.
      element.style.overflow = 'hidden';
      element.style.transition = 'none';
      element.style.maxHeight = `${currentHeight}px`;
      // Second we can transition to the new height.
      element.style.transition = `max-height ${duration}s ease-in-out`;
      // On some browsers (e.g. Firefox), we need to force the calculation of the layout by calling `getBoundingClientRect`
      element.getBoundingClientRect();
      element.style.maxHeight = `${targetHeight}px`;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  useEffect(() => {
    if (isCollapsed && !isInitialRender) {
      timerRef.current = setTimeout(() => {
        setIsCollapsing(false);
      }, 500);

      return () => {
        clearTimeout(timerRef.current);
      };
    } if (!isCollapsed) {
      setIsCollapsing(true);
    }
    return noop;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  const handleContentTransitionEnd = useCallback(() => {
    // Last we remove the max-height so that if the content grows, it still works.
    const newHeight = isCollapsed ? '0' : 'none';
    const newDisplay = isCollapsed ? 'none' : 'block';
    const element = collapsableRef?.current;
    element.style.maxHeight = newHeight;
    element.style.display = newDisplay;
    element.style.overflow = 'visible';
    element.setAttribute('aria-hidden', isCollapsed);
  }, [isCollapsed]);

  return (
    <div {...props}>
      <StyledHeader<'div'>
        id={headerId}
        as={headerAs as 'div'}
      >
        {target}
      </StyledHeader>
      <StyledContentWrapper
        id={contentId}
        ref={collapsableRef}
        aria-labelledby={headerId}
        role="region"
        initialized={collapsableRef.current}
        maxHeight={initialContentMaxHeight}
        display={initialContentDisplay}
        overflow={initialOverflow}
        onTransitionEnd={handleContentTransitionEnd}
      >
        {(forceRender || !isCollapsed || isCollapsing) && (typeof content?.props?.children === 'function' ? content?.props?.children() : content)}
      </StyledContentWrapper>
    </div>
  );
};

Collapsable.defaultProps = CollapsableDefaultProps;
Collapsable.Target = Target;
Collapsable.Content = Content;

export default Collapsable;
